'use client';

/**
 * Chat-level operations of the Meridian API client.
 *
 * Split out of `../meridian-api-client.ts` (module-size rule).
 *
 * `getChat` switches endpoint on the base URL: an `entity-chat` base reads from
 * `/messages`, everything else from `/chats`. That is a real routing difference
 * between the two surfaces, not a quirk — changing it silently 404s entity chat.
 */

import { authenticatedGet, authenticatedPost, authenticatedDelete, authenticatedPut } from '../authenticated-fetch';
import type {
  ChatSummary,
  Chat,
  ListChatsResponse,
  GetChatResponse,
  UpdateChatResponse,
  DeleteChatResponse,
  CreateChatRequest,
  CreateChatResponse,
  CognitoSub,
} from '../../db/types';
import { type ApiResponse, buildQueryString, errorMessage, logApiCall, logApiError } from './shared';

export interface ListChatsParams {
  status?: 'active' | 'archived' | 'all';
  limit?: number;
  cursor?: string;
  includeShared?: boolean;
  entityType?: string;
  entityId?: string;
}

export interface UpdateChatParams {
  chatId: string;
  title?: string;
  status?: 'active' | 'archived';
}

/**
 * List chats with pagination support
 */
export async function listChats(
  apiBaseUrl: string,
  params: ListChatsParams = {}
): Promise<ApiResponse<{ chats: ChatSummary[]; cursor: string | null; hasMore: boolean }>> {
  const { status = 'all', limit = 10, cursor, includeShared, entityType, entityId } = params;
  const queryParams = buildQueryString({
    status,
    limit,
    ...(cursor && { cursor }),
    ...(includeShared !== undefined && { includeShared }),
    ...(entityType && { entityType }),
    ...(entityId && { entityId }),
  });

  const url = `${apiBaseUrl}/chats?${queryParams}`;
  logApiCall('GET', url);

  try {
    const response = await authenticatedGet<ListChatsResponse>(url);
    return {
      success: true,
      data: {
        chats: response.data?.chats || [],
        cursor: response.data?.cursor || null,
        hasMore: response.data?.hasMore || false,
      },
    };
  } catch (error) {
    logApiError('GET', url, error);
    return {
      success: false,
      error: errorMessage(error, 'Failed to list chats'),
    };
  }
}

/**
 * Get a single chat with all messages
 */
export async function getChat(
  apiBaseUrl: string,
  chatId: string,
  params: { limit?: number; cursor?: string } = {}
): Promise<ApiResponse<Chat>> {
  const { limit = 10, cursor } = params;
  const queryParams = buildQueryString({
    chatId,
    limit,
    ...(cursor && { cursor }),
  });

  // For entity-chat, use /messages endpoint; for regular chats, use /chats endpoint
  const endpoint = apiBaseUrl.includes('entity-chat') ? 'messages' : 'chats';
  const url = `${apiBaseUrl}/${endpoint}?${queryParams}`;
  logApiCall('GET', url);

  try {
    const response = await authenticatedGet<GetChatResponse>(url);
    return {
      success: true,
      data: response.data,
      cursor: response.cursor ?? null,
      hasMore: response.hasMore ?? false,
    };
  } catch (error) {
    logApiError('GET', url, error);
    return {
      success: false,
      error: errorMessage(error, 'Failed to get chat'),
    };
  }
}

/**
 * Create a new chat
 */
export async function createChat(
  apiBaseUrl: string,
  request: CreateChatRequest
): Promise<ApiResponse<Chat>> {
  const url = `${apiBaseUrl}/chats`;
  logApiCall('POST', url, request);

  try {
    const response = await authenticatedPost<CreateChatResponse>(url, request);
    const data = response.data;
    return {
      success: true,
      data: data
        ? ({
            id: data.chatId,
            userId: '' as CognitoSub,
            entityType: 'chat',
            title: data.title,
            messages: [],
            status: 'active',
            createdAt: data.createdAt,
            updatedAt: data.createdAt,
            lastMessageAt: data.createdAt,
          } as Chat)
        : undefined,
    };
  } catch (error) {
    logApiError('POST', url, error);
    return {
      success: false,
      error: errorMessage(error, 'Failed to create chat'),
    };
  }
}

/**
 * Update chat metadata (title, status, etc.)
 */
export async function updateChat(
  apiBaseUrl: string,
  params: UpdateChatParams
): Promise<ApiResponse<Chat>> {
  const { chatId, ...updateData } = params;
  const url = `${apiBaseUrl}/chats`;
  logApiCall('PUT', url, { chatId, ...updateData });

  try {
    const response = await authenticatedPut<UpdateChatResponse>(url, {
      chatId,
      ...updateData,
    });
    const data = response.data;
    return {
      success: true,
      data: data
        ? ({
            id: data.chatId,
            userId: '' as CognitoSub,
            entityType: 'chat',
            title: data.title,
            messages: [],
            status: 'active',
            updatedAt: data.updatedAt,
            createdAt: data.updatedAt,
            lastMessageAt: data.updatedAt,
          } as Chat)
        : undefined,
    };
  } catch (error) {
    logApiError('PUT', url, error);
    return {
      success: false,
      error: errorMessage(error, 'Failed to update chat'),
    };
  }
}

/**
 * Delete a chat
 */
export async function deleteChat(
  apiBaseUrl: string,
  chatId: string
): Promise<ApiResponse<void>> {
  const queryParams = buildQueryString({ chatId });
  const url = `${apiBaseUrl}/chats?${queryParams}`;
  logApiCall('DELETE', url);

  try {
    await authenticatedDelete<DeleteChatResponse>(url);
    return {
      success: true,
    };
  } catch (error) {
    logApiError('DELETE', url, error);
    return {
      success: false,
      error: errorMessage(error, 'Failed to delete chat'),
    };
  }
}
